import React from "react";
import { ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { StudentModuleProgress } from "@/services/student/module-progress.api";

export interface ModuleProgressActionsCardProps {
  /** True while module progress is loading. */
  progressPending: boolean;
  moduleProgress: StudentModuleProgress | null | undefined;
  onReiniciarClick: () => void;
  reiniciarDisabled: boolean;
  /** When false (mastery-by-content), the module test CTA is not shown. */
  showModuleTestEntrypoint: boolean;
  /** When true, the module test button is enabled; when false, it shows disabled with a tooltip. */
  moduleTestEnabled: boolean;
  onModuleTestClick: () => void;
  className?: string;
}

/**
 * Barra de progresso do módulo + ações (reiniciar, teste) num único card,
 * alinhada ao padrão visual de faixa (progresso à esquerda, ações à direita).
 */
export const ModuleProgressActionsCard: React.FC<
  ModuleProgressActionsCardProps
> = ({
  progressPending,
  moduleProgress,
  onReiniciarClick,
  reiniciarDisabled,
  showModuleTestEntrypoint,
  moduleTestEnabled,
  onModuleTestClick,
  className,
}) => {
  const total = moduleProgress?.totalExercises ?? 0;
  const answered = moduleProgress?.answeredExercises ?? 0;
  const rawPct = moduleProgress?.progressPercentage;
  const pct = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        progressPending
          ? 0
          : rawPct != null
            ? rawPct
            : 0
      )
    )
  );

  return (
    <TooltipProvider>
      <Card
        className={cn(
          "w-full rounded-lg border border-gray-200 bg-white shadow-sm",
          className
        )}
      >
        <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-lg font-bold text-gray-900">
                {progressPending ? "A carregar…" : `${pct}% Concluído`}
              </span>
              <span className="text-sm text-gray-500">
                {progressPending
                  ? "—"
                  : total > 0
                    ? `${answered} de ${total} questões`
                    : "0 de 0 questões"}
              </span>
            </div>
            <div
              className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Progresso do módulo"
            >
              <div
                className="h-full rounded-full bg-gray-900 transition-[width] duration-300 ease-out"
                style={{ width: `${progressPending ? 0 : pct}%` }}
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-10 gap-2 border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
              onClick={onReiniciarClick}
              disabled={reiniciarDisabled}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Reiniciar módulo
            </Button>

            {showModuleTestEntrypoint && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* span wrapper needed so Tooltip works on a disabled button */}
                <span tabIndex={moduleTestEnabled ? -1 : 0} className="inline-flex">
                  <Button
                    type="button"
                    className="h-10 gap-2 bg-green-600 text-white hover:bg-green-700 disabled:pointer-events-none disabled:opacity-50"
                    onClick={onModuleTestClick}
                    disabled={!moduleTestEnabled}
                  >
                    Fazer teste do módulo
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Button>
                </span>
              </TooltipTrigger>
              {!moduleTestEnabled && (
                <TooltipContent>
                  <p>Complete todos os exercícios para fazer o teste</p>
                </TooltipContent>
              )}
            </Tooltip>
            )}
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
};
